import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import ConversationListItem from './components/ConversationListItem';
import MessageBubble from './components/MessageBubble';
import MessageInput from './components/MessageInput';
import ConversationHeader from './components/ConversationHeader';

import { messagingService } from '../../services/messagingService';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { MESSAGE_STATUS } from '../../utils/messageQueue';

// Utility function to remove duplicate names
const cleanDuplicateName = (name) => {
  if (!name) return 'İsimsiz Kullanıcı';
  
  // Trim whitespace
  const trimmed = name?.trim();
  
  // Split by spaces and check for duplicates
  const words = trimmed?.split(/\s+/);
  
  // If the name is repeated (e.g., "John Doe John Doe"), take only the first half
  if (words?.length > 1 && words?.length % 2 === 0) {
    const halfLength = words?.length / 2;
    const firstHalf = words?.slice(0, halfLength)?.join(' ');
    const secondHalf = words?.slice(halfLength)?.join(' ');
    
    // If both halves are identical, return only one
    if (firstHalf === secondHalf) {
      return firstHalf;
    }
  }
  
  return trimmed;
};

const MessagingCenter = () => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showMobileList, setShowMobileList] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [queueStatus, setQueueStatus] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const messagesEndRef = useRef(null);
  const conversationChannelRef = useRef(null);
  const notificationChannelRef = useRef(null);
  
  // CRITICAL FIX: Store current user ID in ref to avoid closure issues
  const currentUserIdRef = useRef(null);

  // Update ref when user changes
  useEffect(() => {
    if (user?.id) {
      currentUserIdRef.current = user?.id;
      console.log('👤 Current user ID updated:', user?.id);
    }
  }, [user?.id]);

  // Handle startChatWith from navigation state with DUPLICATE-PROOF logic
  useEffect(() => {
    const initChat = async () => {
      const state = location?.state;
      
      // Only proceed if we have a target user ID
      if (state && state?.startChatWith) {
        // Clear history state immediately to prevent re-trigger on simple re-renders
        window.history?.replaceState({}, document.title);
        
        try {
          // 1. Get or Create the conversation via service
          const conversationId = await messagingService?.getOrCreateDirectConversation(state?.startChatWith);
          
          if (!conversationId) {
            console.error("Chat init failed: No conversation ID returned");
            return;
          }
          
          // 2. Fetch user profile for the conversation
          const { data: userProfile, error: profileError } = await supabase?.from('user_profiles')?.select('id, full_name, avatar_url')?.eq('id', state?.startChatWith)?.single();
          
          if (profileError || !userProfile) {
            console.error("Chat init failed:", profileError?.message || "User not found");
            return;
          }
          
          // 3. Create conversation object
          const newConversation = {
            id: conversationId,
            name: cleanDuplicateName(userProfile?.full_name),
            avatar: userProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(cleanDuplicateName(userProfile?.full_name) || 'User')}&background=0D8ABC&color=fff`,
            lastMessage: 'Henüz mesaj yok',
            lastMessageTime: new Date()?.toISOString(),
            unreadCount: 0,
            type: 'direct',
            userId: userProfile?.id
          };
          
          // 4. STATE UPDATE WITH DUPLICATE CHECK
          setConversations(prevConversations => {
            // Check if this conversation ID already exists in the list
            const exists = prevConversations?.some(c => c?.id === newConversation?.id);
            
            if (exists) {
              // If exists, do NOT add it again, just return the current list
              console.log('✅ Conversation already exists, skipping duplicate');
              return prevConversations;
            }
            // If not exists, add to the top
            console.log('✅ Adding new conversation to list');
            return [newConversation, ...prevConversations];
          });
          
          // 5. Select the conversation
          setActiveConversation(newConversation);
          setShowMobileList(false);
          setMessages([]);
          
        } catch (error) {
          console.error("Error initializing chat:", error);
        }
      }
    };
    
    initChat();
  }, [location]); // Keep dependency simple

  // Create new conversation with a specific user
  const createNewConversation = async (userId) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🚀 Creating conversation with user:', userId);
      
      // CRITICAL FIX: Use messaging service to get or create conversation
      const conversationId = await messagingService?.getOrCreateDirectConversation(userId);
      
      if (!conversationId) {
        throw new Error('Sohbet oluşturulamadı');
      }
      
      console.log('✅ Conversation created/found:', conversationId);
      
      // CRITICAL FIX: Fetch user profile using supabase directly (not through messagingService)
      const { data: userProfile, error: profileError } = await supabase?.from('user_profiles')?.select('id, full_name, avatar_url')?.eq('id', userId)?.single();
      
      if (profileError) {
        console.error('❌ Error fetching user profile:', profileError);
        throw new Error(`Kullanıcı bilgileri alınamadı: ${profileError.message}`);
      }
      
      if (!userProfile) {
        throw new Error('Kullanıcı bulunamadı');
      }
      
      console.log('✅ User profile fetched:', userProfile?.full_name);
      
      // CRITICAL FIX: Create conversation object with correct structure
      const newConversation = {
        id: conversationId,
        name: cleanDuplicateName(userProfile?.full_name),
        avatar: userProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(cleanDuplicateName(userProfile?.full_name) || 'User')}&background=0D8ABC&color=fff`,
        lastMessage: 'Henüz mesaj yok',
        lastMessageTime: new Date()?.toISOString(),
        unreadCount: 0,
        type: 'direct',
        userId: userProfile?.id
      };
      
      console.log('✅ New conversation object created:', newConversation);
      
      // Add to conversations list at the top
      setConversations(prev => [newConversation, ...prev]);
      
      // Open the conversation
      setActiveConversation(newConversation);
      setShowMobileList(false);
      
      // Load messages (will be empty for new conversation)
      setMessages([]);
      
      console.log('✅ Conversation opened successfully');
      
    } catch (err) {
      console.error('❌ Error creating new conversation:', err);
      const errorMessage = err?.message || 'Sohbet başlatılamadı';
      setError(errorMessage);
      alert(`Bir hata oluştu\n\n${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Utility function to scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // CRITICAL FIX: Memoized callback with proper closure handling
  const handleNewMessage = useCallback((message) => {
    console.log('📨 New message received:', message?.id, 'from:', message?.senderId);
    
    const currentUserId = currentUserIdRef?.current;
    
    if (!currentUserId) {
      console.warn('⚠️ Current user ID not available');
      return;
    }
    
    // CRITICAL FIX: Filter out messages from current user to prevent duplicates
    // Messages from current user are already handled by optimistic UI
    if (message?.senderId === currentUserId) {
      console.log('⏭️ Message is from current user, checking for optimistic update');
      
      // Update existing optimistic message with real data if it exists
      setMessages((prev) => {
        const existingIndex = prev?.findIndex(
          (msg) => msg?.tempId && msg?.content === message?.content && msg?.senderId === currentUserId
        );
        
        if (existingIndex !== -1) {
          console.log('✅ Replacing optimistic message with real message');
          const updated = [...prev];
          updated[existingIndex] = {
            ...message,
            timestamp: message?.createdAt
          };
          return updated;
        }
        
        // Check if message already exists by ID
        const messageExists = prev?.some(msg => msg?.id === message?.id);
        if (messageExists) {
          console.log('⏭️ Message already exists, skipping');
          return prev;
        }
        
        // If no matching optimistic message and not duplicate, it's a stale real-time event
        console.log('⏭️ No matching optimistic message, skipping to avoid duplicate');
        return prev;
      });
      
      return;
    }
    
    // CRITICAL FIX: Only add messages from OTHER users
    console.log('✅ Adding message from other user:', message?.sender?.name);
    setMessages((prev) => {
      // Check if message already exists (prevent duplicates)
      const messageExists = prev?.some(msg => msg?.id === message?.id);
      if (messageExists) {
        console.log('⏭️ Message already exists, skipping');
        return prev;
      }
      
      return [...prev, { ...message, timestamp: message?.createdAt }];
    });
    
    // Update conversation last message in the list
    setConversations((prev) =>
      prev?.map((conv) =>
        conv?.id === activeConversation?.id
          ? {
              ...conv,
              lastMessage: message?.content,
              lastMessageTime: message?.createdAt,
              unreadCount: 0 // Reset since we're viewing it
            }
          : conv
      )
    );

    setTimeout(scrollToBottom, 100);
  }, []); // Empty deps - using ref for user ID to avoid closure issues

  // Handle new notification from real-time subscription
  const handleNewNotification = (notification) => {
    console.log('🔔 New notification received:', notification);
    
    // Show browser notification if permitted
    if (Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico'
      });
    }

    // Reload conversations to update unread counts
    loadConversations();
  };

  // Load conversations on mount - only when authenticated
  useEffect(() => {
    if (!authLoading && user) {
      console.log('👤 User authenticated, loading conversations...');
      loadConversations();
    } else if (!authLoading && !user) {
      console.log('⚠️ User not authenticated');
      setLoading(false);
    }
  }, [authLoading, user]);

  // Subscribe to real-time notifications when user is authenticated
  useEffect(() => {
    if (!user) return;

    console.log('🔔 Setting up notification subscription...');
    const channel = messagingService?.subscribeToNotifications(handleNewNotification);
    notificationChannelRef.current = channel;

    return () => {
      const currentChannel = notificationChannelRef?.current;
      if (currentChannel) {
        console.log('🔕 Cleaning up notification subscription...');
        messagingService?.unsubscribe(currentChannel);
      }
    };
  }, [user]);

  // Load messages and subscribe when conversation changes
  useEffect(() => {
    if (activeConversation) {
      console.log('📖 Loading messages for conversation:', activeConversation?.id);
      loadMessages(activeConversation?.id);
      
      // Subscribe to real-time messages
      const channel = messagingService?.subscribeToConversation(
        activeConversation?.id,
        handleNewMessage
      );
      conversationChannelRef.current = channel;
    }

    return () => {
      const currentChannel = conversationChannelRef?.current;
      if (currentChannel) {
        console.log('🔕 Cleaning up conversation subscription...');
        messagingService?.unsubscribe(currentChannel);
      }
    };
  }, [activeConversation]);

  // Monitor queue status
  useEffect(() => {
    const updateQueueStatus = () => {
      const status = messagingService?.getQueueStatus();
      setQueueStatus(status);
    };

    // Update immediately
    updateQueueStatus();

    // Update periodically
    const interval = setInterval(updateQueueStatus, 3000);

    return () => clearInterval(interval);
  }, []);

  const loadConversations = async () => {
    if (!user) {
      setError('Mesajları görmek için giriş yapmalısınız.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const data = await messagingService?.getMyConversations();
      
      // Transform data to match component expectations
      const transformedConversations = data?.map(conv => ({
        id: conv?.conversationId,
        name: cleanDuplicateName(conv?.userName),
        avatar: conv?.userAvatar,
        lastMessage: conv?.lastMessage,
        lastMessageTime: conv?.lastMessageTime,
        unreadCount: conv?.unreadCount,
        type: conv?.type,
        userId: conv?.userId
      }));
      
      setConversations(transformedConversations);
      console.log(`✅ Loaded ${transformedConversations?.length} conversations`);

    } catch (err) {
      console.error('❌ Error loading conversations:', err);
      setError(err?.message || 'Sohbetler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const data = await messagingService?.getConversationMessages(conversationId);
      
      // Transform data to match component expectations
      const transformedMessages = data?.map(msg => ({
        id: msg?.id,
        senderId: msg?.senderId,
        content: msg?.content,
        timestamp: msg?.createdAt,
        type: msg?.messageType,
        sender: msg?.sender
      }));
      
      setMessages(transformedMessages);
      setTimeout(scrollToBottom, 100);

    } catch (err) {
      console.error('❌ Error loading messages:', err);
      setError(err?.message || 'Mesajlar yüklenirken bir hata oluştu.');
    }
  };

  const handleConversationClick = async (conversation) => {
    console.log('💬 Opening conversation:', conversation?.id);
    
    // Set active conversation first
    setActiveConversation(conversation);
    setShowMobileList(false);
    
    // Clear any previous errors
    setError('');
    
    // Mark conversation as read
    try {
      await messagingService?.markConversationAsRead(conversation?.id);
      
      // Update unread count in local state
      setConversations((prev) =>
        prev?.map((conv) =>
          conv?.id === conversation?.id ? { ...conv, unreadCount: 0 } : conv
        )
      );
    } catch (err) {
      console.error('Error marking conversation as read:', err);
      // Don't show error to user for this - it's not critical
    }
  };

  const handleSendMessage = async (messageContent) => {
    // CRITICAL FIX: Accept message content as parameter instead of using state
    if (!messageContent?.trim() || sendingMessage || !activeConversation) return;

    const messageText = messageContent?.trim();
    const tempId = `temp_${Date.now()}`;

    // Optimistic UI update
    const optimisticMessage = {
      id: tempId,
      tempId,
      senderId: user?.id,
      content: messageText,
      messageType: 'text',
      isRead: false,
      createdAt: new Date()?.toISOString(),
      status: MESSAGE_STATUS?.PENDING,
      isQueued: true,
      sender: {
        id: user?.id,
        name: 'You',
        avatar: user?.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=You&background=0D8ABC&color=fff`,
        email: user?.email || ''
      }
    };

    setMessages(prev => [...prev, optimisticMessage]);
    setSendingMessage(true);
    
    // CRITICAL FIX: Scroll to bottom after adding optimistic message
    setTimeout(scrollToBottom, 100);

    try {
      const sentMessage = await messagingService?.sendMessage(
        activeConversation?.id,
        messageText,
        { tempId }
      );

      // Update optimistic message with real data
      setMessages(prev => 
        prev?.map(msg => 
          msg?.tempId === tempId 
            ? { ...sentMessage, tempId, timestamp: sentMessage?.createdAt } 
            : msg
        )
      );

      // Subscribe to status updates if queued
      if (sentMessage?.isQueued) {
        messagingService?.onMessageStatusUpdate(sentMessage?.id, (status, updatedMessage) => {
          setMessages(prev =>
            prev?.map(msg =>
              msg?.id === sentMessage?.id || msg?.tempId === tempId
                ? { ...msg, status, ...updatedMessage }
                : msg
            )
          );

          // Remove temp message when sent successfully
          if (status === MESSAGE_STATUS?.SENT) {
            setTimeout(() => {
              messagingService?.offMessageStatusUpdate(sentMessage?.id);
            }, 2000);
          }
        });
      }

      // Update conversation list with new last message
      setConversations((prev) =>
        prev?.map((conv) =>
          conv?.id === activeConversation?.id
            ? {
                ...conv,
                lastMessage: messageText,
                lastMessageTime: new Date()?.toISOString(),
                unreadCount: 0
              }
            : conv
        )
      );

      // Mark conversation as read
      await messagingService?.markConversationAsRead(activeConversation?.id);
      setError(null);

    } catch (error) {
      console.error('Error sending message:', error);
      
      // Update message status to failed
      setMessages(prev =>
        prev?.map(msg =>
          msg?.tempId === tempId
            ? { ...msg, status: MESSAGE_STATUS?.FAILED, error: error?.message }
            : msg
        )
      );
      
      setError(error?.message || 'Mesaj gönderilemedi. Tekrar deneyin.');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleEditMessage = async (messageId, newContent) => {
    try {
      setError('');
      
      const updatedMessage = await messagingService?.editMessage(messageId, newContent);
      
      // Update message in local state
      setMessages(prev =>
        prev?.map(msg =>
          msg?.id === messageId
            ? { 
                ...msg, 
                content: updatedMessage?.content,
                updatedAt: updatedMessage?.updatedAt,
                isEdited: true
              }
            : msg
        )
      );

      // Update last message in conversation list if it was the last message
      const lastMessage = messages?.[messages?.length - 1];
      if (lastMessage?.id === messageId) {
        setConversations(prev =>
          prev?.map(conv =>
            conv?.id === activeConversation?.id
              ? { ...conv, lastMessage: newContent }
              : conv
          )
        );
      }

    } catch (error) {
      console.error('Error editing message:', error);
      setError(error?.message || 'Mesaj düzenlenemedi.');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      setError('');
      
      await messagingService?.deleteMessage(messageId);
      
      // Remove message from local state
      setMessages(prev => prev?.filter(msg => msg?.id !== messageId));

      // Update last message in conversation list if needed
      const remainingMessages = messages?.filter(msg => msg?.id !== messageId);
      if (remainingMessages?.length > 0) {
        const lastMessage = remainingMessages?.[remainingMessages?.length - 1];
        setConversations(prev =>
          prev?.map(conv =>
            conv?.id === activeConversation?.id
              ? { 
                  ...conv, 
                  lastMessage: lastMessage?.content,
                  lastMessageTime: lastMessage?.createdAt 
                }
              : conv
          )
        );
      } else {
        setConversations(prev =>
          prev?.map(conv =>
            conv?.id === activeConversation?.id
              ? { ...conv, lastMessage: 'Henüz mesaj yok' }
              : conv
          )
        );
      }

    } catch (error) {
      console.error('Error deleting message:', error);
      setError(error?.message || 'Mesaj silinemedi.');
    }
  };

  const handleDeleteConversation = async () => {
    if (!activeConversation) return;

    try {
      setError('');
      setShowDeleteConfirm(false);
      
      await messagingService?.deleteConversation(activeConversation?.id);
      
      // Remove conversation from local state
      setConversations(prev => prev?.filter(conv => conv?.id !== activeConversation?.id));
      
      // Clear active conversation
      setActiveConversation(null);
      setMessages([]);
      setShowMobileList(true);

    } catch (error) {
      console.error('Error deleting conversation:', error);
      setError(error?.message || 'Sohbet silinemedi. Sadece oluşturduğunuz sohbetleri silebilirsiniz.');
    }
  };

  const handleBack = () => {
    setShowMobileList(true);
    setActiveConversation(null);
  };

  const handleSearch = () => {
    // Implement search functionality if needed
  };

  const handleBlock = () => {
    alert(`${activeConversation?.name} engellendi`);
  };

  const handleReport = () => {
    alert(`${activeConversation?.name} şikayet edildi`);
  };

  // Filter conversations based on search and filter type
  const filteredConversations = conversations?.filter((conv) => {
    const matchesSearch =
      conv?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      conv?.lastMessage?.toLowerCase()?.includes(searchQuery?.toLowerCase());

    const matchesFilter =
      filterType === 'all' ||
      (filterType === 'unread' && conv?.unreadCount > 0) ||
      (filterType === 'groups' && conv?.type === 'group') ||
      (filterType === 'organizers' && conv?.type === 'organizer');

    return matchesSearch && matchesFilter;
  });

  const totalUnread = conversations?.reduce((sum, conv) => sum + conv?.unreadCount, 0);

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-24 flex items-center justify-center h-[calc(100vh-60px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-900">
              {authLoading ? 'Kimlik doğrulanıyor...' : 'Mesajlar yükleniyor...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated state
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-24 flex items-center justify-center h-[calc(100vh-60px)]">
          <div className="text-center">
            <Icon name="Lock" size={48} color="#6b7280" className="mx-auto mb-4" />
            <p className="text-gray-900 mb-2 text-xl font-semibold">
              Giriş Yapmalısınız
            </p>
            <p className="text-gray-500 mb-4">
              Mesajlarınızı görmek için lütfen giriş yapın
            </p>
            <button
              onClick={() => window.location.href = '/login'}
              className="px-6 py-3 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              Giriş Yap
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-24 flex items-center justify-center h-[calc(100vh-60px)]">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} color="#dc2626" className="mx-auto mb-4" />
            <p className="text-red-600 mb-2">Bir hata oluştu</p>
            <p className="text-gray-500 mb-4">{error}</p>
            <button
              onClick={loadConversations}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              Tekrar Dene
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <Header />
      
      {/* Delete Conversation Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-2 text-gray-900">
              Sohbeti Sil
            </h3>
            <p className="text-sm mb-4 text-gray-600">
              Bu sohbeti ve tüm mesajları kalıcı olarak silmek istediğinizden emin misiniz? 
              Bu işlem geri alınamaz.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleDeleteConversation}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Container - Adjusted pt-16 for proper header clearance */}
      <div className="pt-16 flex-1 flex overflow-hidden max-w-[1600px] mx-auto w-full">
        {/* Conversations Sidebar */}
        <div 
          className={`${showMobileList ? 'flex' : 'hidden'} lg:flex flex-col w-full lg:w-[420px] bg-white border-r border-gray-200`}
        >
          {/* Sidebar Header */}
          <div className="flex flex-col bg-white border-b border-gray-200">
            {/* Title and Actions */}
            <div className="flex items-center justify-between px-6 py-5">
              <h1 className="text-2xl font-semibold text-gray-900">
                Mesajlar
              </h1>
              <div className="flex items-center gap-4">
                <button 
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  title="Yeni sohbet"
                >
                  <Icon name="MessageSquarePlus" size={22} color="#4b5563" />
                </button>
                <button 
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  title="Daha fazla"
                >
                  <Icon name="MoreVertical" size={22} color="#4b5563" />
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="px-4 pb-4">
              <div className="relative">
                <Icon
                  name="Search"
                  size={18}
                  color="#6b7280"
                  className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none"
                />
                <Input
                  type="search"
                  placeholder="Ara veya yeni sohbet başlat"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-12 pr-4 py-2.5 rounded-lg text-sm bg-gray-50 border-0 text-gray-900 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2 px-4 pb-3 overflow-x-auto">
              {[
                { value: 'all', label: 'Tümü' },
                { value: 'unread', label: 'Okunmamış' },
                { value: 'groups', label: 'Gruplar' }
              ]?.map((filter) => {
                const isActive = filterType === filter?.value;
                const count = filter?.value === 'unread' 
                  ? conversations?.filter((c) => c?.unreadCount > 0)?.length
                  : filter?.value === 'groups'
                  ? conversations?.filter((c) => c?.type === 'group')?.length
                  : conversations?.length;

                return (
                  <button
                    key={filter?.value}
                    onClick={() => setFilterType(filter?.value)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                      isActive 
                        ? 'bg-blue-50 text-blue-600' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {filter?.label}
                    {count > 0 && ` (${count})`}
                  </button>
                );
              })}
            </div>

            {/* Queue Status */}
            {queueStatus && queueStatus?.pending > 0 && (
              <div className="mx-4 mb-2 flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-amber-50 text-amber-700">
                <Icon name="RefreshCw" size={14} className="animate-spin" />
                <span>{queueStatus?.pending} mesaj gönderiliyor...</span>
              </div>
            )}
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredConversations?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-gray-100">
                  <Icon name="MessageCircle" size={32} color="#6b7280" />
                </div>
                <p className="text-lg font-medium text-gray-700">
                  {searchQuery ? 'Sonuç bulunamadı' : 'Henüz mesajınız yok'}
                </p>
                <p className="text-sm mt-1 text-gray-500">
                  {searchQuery ? 'Başka bir arama deneyin' : 'Yeni bir sohbet başlatın'}
                </p>
              </div>
            ) : (
              filteredConversations?.map((conversation) => (
                <ConversationListItem
                  key={conversation?.id}
                  conversation={conversation}
                  isActive={activeConversation?.id === conversation?.id}
                  onSelect={handleConversationClick}
                />
              ))
            )}
          </div>
        </div>

        {/* Chat Area - Removed doodle background */}
        <div 
          className={`${showMobileList ? 'hidden' : 'flex'} lg:flex flex-col flex-1 bg-gray-50`}
        >
          {activeConversation ? (
            <>
              {/* Chat Header */}
              <ConversationHeader
                conversation={activeConversation}
                onBack={handleBack}
                onSearch={handleSearch}
                onBlock={handleBlock}
                onReport={handleReport}
                onDelete={() => setShowDeleteConfirm(true)}
              />

              {/* Messages Container - Clean gray background instead of doodle pattern */}
              <div className="flex-1 overflow-y-auto bg-gray-50">
                <div className="max-w-[1100px] mx-auto px-4 md:px-[8%] py-6">
                  {messages?.length === 0 ? (
                    <div className="flex items-center justify-center py-20">
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4 bg-blue-50">
                          <Icon name="MessageCircle" size={32} color="#2563eb" />
                        </div>
                        <p className="text-lg font-medium text-gray-600">
                          İlk mesajınızı gönderin
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {messages?.map((message, index) => {
                        const showAvatar = index === 0 || 
                          messages?.[index - 1]?.senderId !== message?.senderId;
                        const showTimestamp = index === messages?.length - 1 ||
                          messages?.[index + 1]?.senderId !== message?.senderId;
                        
                        return (
                          <MessageBubble
                            key={message?.id}
                            message={message}
                            isCurrentUser={message?.senderId === user?.id}
                            showAvatar={showAvatar}
                            showTimestamp={showTimestamp}
                            onEdit={handleEditMessage}
                            onDelete={handleDeleteMessage}
                          />
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>
              </div>

              {/* Error Banner */}
              {error && (
                <div className="px-4 py-3 flex items-center justify-between bg-red-50 border-t border-red-200">
                  <div className="flex items-center gap-2">
                    <Icon name="AlertCircle" size={16} color="#dc2626" />
                    <span className="text-sm text-red-600">
                      {error}
                    </span>
                  </div>
                  <button 
                    onClick={() => setError('')}
                    className="p-1 hover:bg-red-100 rounded transition-colors"
                  >
                    <Icon name="X" size={16} color="#dc2626" />
                  </button>
                </div>
              )}

              {/* Message Input */}
              <MessageInput
                onSendMessage={handleSendMessage}
                onImageUpload={() => {}}
              />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-white border-l border-gray-200">
              <div className="text-center px-6 max-w-md">
                <div className="w-80 h-80 mx-auto mb-8 rounded-full flex items-center justify-center bg-gray-50">
                  <Icon name="MessageSquare" size={120} color="#9ca3af" />
                </div>
                <h2 className="text-3xl font-light mb-4 text-gray-700">
                  Profesyonel Mesajlaşma
                </h2>
                <p className="text-sm text-gray-500">
                  Mesajları gönderin ve alın, fotoğraf ve video paylaşın, sesli mesajlar gönderin ve daha fazlasını yapın.
                </p>
                <div className="mt-8 p-4 rounded-lg bg-blue-50">
                  <p className="text-xs text-gray-600">
                    🔒 Güvenli ve şifrelenmiş mesajlaşma
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagingCenter;